import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { LoadingPage, Spinner } from '../../components/common/Spinner';
import { Button } from '../../components/common/Button';
import { AppAccessStatusChip } from '../../components/app-access/AppAccessStatusChip';
import { AppAccessDrawer } from '../../components/app-access/AppAccessDrawer';
import { RevokeConfirmModal } from '../../components/app-access/RevokeConfirmModal';
import { AppToast, type AppToastState } from '../../components/app-access/AppToast';
import { BulkActionBar } from '../../components/app-access/BulkActionBar';
import {
  BulkSummaryModal,
  type BulkSummaryItem,
  type BulkSkippedSummary,
} from '../../components/app-access/BulkSummaryModal';
import {
  useAppAccessMatrix,
  useGrantAccess,
  useRevokeAccess,
  useRetryAccess,
  useBulkGrant,
  useBulkRevoke,
} from '../../hooks/useAppAccess';
import {
  SYSTEMS,
  SYSTEM_DISPLAY_NAMES,
  isBulkExcludedSystem,
  type AppAccessSystem,
  type AppAccessStatus,
  type AppAccessTier,
  type MatrixCell,
  type MatrixRow,
  type BulkPair,
} from '../../services/api/appAccess';
import { ApiClientError } from '../../services/api/client';

type StatusFilter = 'all' | AppAccessStatus;

const FILTER_OPTIONS: Array<{ value: StatusFilter; label: string }> = [
  { value: 'all', label: 'All' },
  { value: 'granted', label: 'Granted' },
  { value: 'not_granted', label: 'Not granted' },
  { value: 'pending', label: 'Pending' },
  { value: 'failed', label: 'Failed' },
];

const PAGE_SIZE = 50;

export default function AppAccessMatrixPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // Read URL into local state; URL is canonical view state.
  const initialSearch = searchParams.get('search') ?? '';
  const initialStatus = (searchParams.get('status') ?? 'all') as StatusFilter;
  const initialPage = parseInt(searchParams.get('page') ?? '1', 10) || 1;

  const [searchQuery, setSearchQuery] = useState(initialSearch);
  const [debouncedSearch, setDebouncedSearch] = useState(initialSearch);
  const [statusFilter, setStatusFilter] = useState<StatusFilter>(initialStatus);
  const [page, setPage] = useState(initialPage);

  // Debounce search keystrokes (Pitfall #8 — clearTimeout in cleanup).
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(searchQuery), 300);
    return () => clearTimeout(t);
  }, [searchQuery]);

  // Sync URL when debounced search / status / page change (Pattern 2 hybrid).
  useEffect(() => {
    const params = new URLSearchParams();
    if (debouncedSearch) params.set('search', debouncedSearch); // Pitfall #6 — omit when empty
    if (statusFilter !== 'all') params.set('status', statusFilter);
    if (page > 1) params.set('page', String(page));
    setSearchParams(params, { replace: true });
  }, [debouncedSearch, statusFilter, page, setSearchParams]);

  // Reset to page 1 on search or status change (Pitfall #7).
  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, statusFilter]);

  // Matrix query
  const filters = useMemo(
    () => ({ search: debouncedSearch, status: statusFilter, page, pageSize: PAGE_SIZE }),
    [debouncedSearch, statusFilter, page]
  );
  const { data, isLoading, isFetching, error, refetch } = useAppAccessMatrix(filters);

  // Drawer + Revoke modal + Toast state (page-owned)
  const [drawerState, setDrawerState] = useState<{ row: MatrixRow; system: AppAccessSystem } | null>(
    null
  );
  const [revokeOpen, setRevokeOpen] = useState(false);
  const [revokeMode, setRevokeMode] = useState<'dispatch' | 'manual'>('dispatch');
  const [revokeError, setRevokeError] = useState<string | null>(null);
  const [toast, setToast] = useState<AppToastState | null>(null);

  // The originating cell button ref — captured at click time so focus returns correctly on close.
  const originatingButtonRef = useRef<HTMLButtonElement | null>(null);

  // Mutations
  const grantMutation = useGrantAccess();
  const revokeMutation = useRevokeAccess();
  const retryMutation = useRetryAccess();
  const bulkGrantMutation = useBulkGrant();
  const bulkRevokeMutation = useBulkRevoke();

  // ─────────────────────────────────────────────────────────────
  // Bulk selection state (D-01, D-02)
  // Keys are `${userId}:${system}` for O(1) .has() lookups.
  // ─────────────────────────────────────────────────────────────
  const [selectedSet, setSelectedSet] = useState<Set<string>>(new Set());
  const [pendingDirection, setPendingDirection] = useState<'grant' | 'revoke' | null>(null);

  const cellKey = (userId: number, system: AppAccessSystem) => `${userId}:${system}`;
  const parseCellKey = (key: string): BulkPair => {
    const colonIdx = key.indexOf(':');
    const u = key.slice(0, colonIdx);
    const s = key.slice(colonIdx + 1);
    return { userId: parseInt(u, 10), system: s as AppAccessSystem };
  };

  const toggleCell = useCallback((userId: number, system: AppAccessSystem) => {
    setSelectedSet((prev) => {
      const next = new Set(prev);
      const k = cellKey(userId, system);
      if (next.has(k)) next.delete(k);
      else next.add(k);
      return next;
    });
  }, []);

  const toggleColumn = useCallback(
    (system: AppAccessSystem, visibleRows: { user: { id: number } }[]) => {
      if (isBulkExcludedSystem(system)) return; // D-04 — auto-managed + claude have no bulk checkbox
      setSelectedSet((prev) => {
        const next = new Set(prev);
        const allKeys = visibleRows.map((r) => cellKey(r.user.id, system));
        const allSelected = allKeys.every((k) => next.has(k));
        if (allSelected) {
          allKeys.forEach((k) => next.delete(k));
        } else {
          allKeys.forEach((k) => next.add(k));
        }
        return next;
      });
    },
    []
  );

  const clearSelection = useCallback(() => setSelectedSet(new Set()), []);

  // D-01: clear selection whenever filter params or page change
  useEffect(() => {
    clearSelection();
  }, [searchQuery, statusFilter, page, clearSelection]);

  // Eligibility computed reactively from page data + selection (PITFALL-02)
  const eligibility = useMemo(() => {
    let hasGrantEligible = false;
    let hasRevokeEligible = false;
    for (const key of selectedSet) {
      const { userId, system } = parseCellKey(key);
      if (isBulkExcludedSystem(system)) continue;
      const row = data?.data.find((r) => r.user.id === userId);
      const cell = row?.cells[system];
      if (!cell) continue;
      if (cell.status === 'not_granted' || cell.status === 'failed') hasGrantEligible = true;
      if (cell.status === 'granted' || cell.status === 'assumed_granted') hasRevokeEligible = true;
      if (hasGrantEligible && hasRevokeEligible) break;
    }
    return { hasGrantEligible, hasRevokeEligible };
  }, [selectedSet, data]);

  // Build summary for the modal — group eligible cells per system, count skipped reasons
  const bulkSummary = useMemo<{
    perSystem: BulkSummaryItem[];
    skipped: BulkSkippedSummary;
    eligibleCount: number;
  }>(() => {
    if (!pendingDirection)
      return {
        perSystem: [],
        skipped: { alreadyInTargetState: 0, pending: 0, ineligible: 0, autoManaged: 0 },
        eligibleCount: 0,
      };
    const perSystemMap = new Map<
      AppAccessSystem,
      { count: number; sample: string[]; remaining: number }
    >();
    let alreadyInTargetState = 0,
      pending = 0,
      ineligible = 0,
      autoManaged = 0;
    for (const key of selectedSet) {
      const { userId, system } = parseCellKey(key);
      if (isBulkExcludedSystem(system)) {
        autoManaged += 1;
        continue;
      }
      const row = data?.data.find((r) => r.user.id === userId);
      const cell = row?.cells[system];
      if (!cell || !row) {
        ineligible += 1;
        continue;
      }
      if (cell.status === 'pending') {
        pending += 1;
        continue;
      }
      const eligible =
        pendingDirection === 'grant'
          ? cell.status === 'not_granted' || cell.status === 'failed'
          : cell.status === 'granted' || cell.status === 'assumed_granted';
      if (!eligible) {
        alreadyInTargetState += 1;
        continue;
      }
      const userLabel = row.user.email;
      const entry = perSystemMap.get(system) ?? { count: 0, sample: [], remaining: 0 };
      entry.count += 1;
      if (entry.sample.length < 5) entry.sample.push(userLabel);
      else entry.remaining += 1;
      perSystemMap.set(system, entry);
    }
    const perSystem: BulkSummaryItem[] = SYSTEMS.filter(
      (s) => !isBulkExcludedSystem(s) && perSystemMap.has(s)
    ).map((s) => {
      const e = perSystemMap.get(s)!;
      return {
        system: s,
        systemDisplayName: SYSTEM_DISPLAY_NAMES[s],
        count: e.count,
        sampleUserLabels: e.sample,
        remainingCount: e.remaining,
      };
    });
    const eligibleCount = perSystem.reduce((acc, it) => acc + it.count, 0);
    return {
      perSystem,
      skipped: { alreadyInTargetState, pending, ineligible, autoManaged },
      eligibleCount,
    };
  }, [selectedSet, data, pendingDirection]);

  const handleBulkConfirm = async () => {
    if (!pendingDirection) return;
    // Build pairs[] from eligible cells only (server is the source of truth — UI filters for parity)
    const pairs: BulkPair[] = [];
    for (const key of selectedSet) {
      const { userId, system } = parseCellKey(key);
      if (isBulkExcludedSystem(system)) continue;
      const row = data?.data.find((r) => r.user.id === userId);
      const cell = row?.cells[system];
      if (!cell) continue;
      if (cell.status === 'pending') continue;
      const eligible =
        pendingDirection === 'grant'
          ? cell.status === 'not_granted' || cell.status === 'failed'
          : cell.status === 'granted' || cell.status === 'assumed_granted';
      if (!eligible) continue;
      pairs.push({ userId, system });
    }
    try {
      const mutation = pendingDirection === 'grant' ? bulkGrantMutation : bulkRevokeMutation;
      const result = await mutation.mutateAsync(pairs);
      setToast({
        message: `Bulk ${pendingDirection} dispatched — ${result.dispatched} cells (op ${result.bulkOperationId.slice(0, 6)})`,
        variant: 'success',
      });
      setSelectedSet(new Set());
      setPendingDirection(null);
    } catch (err) {
      setToast({ message: (err as Error).message, variant: 'error' });
    }
  };

  const closeDrawer = () => {
    setDrawerState(null);
    setRevokeOpen(false);
    setRevokeError(null);
  };

  const fullNameOf = (u: MatrixRow['user']) =>
    `${u.firstName ?? ''} ${u.lastName ?? ''}`.trim() || u.email;

  // Matrix user-identity column convention is lastName-first (UI-SPEC line 165-169;
  // analog: frontend/src/pages/admin/UserRoleMatrixPage.tsx:42; MEMORY.md "PM name format in UI: Lastname Firstname").
  // Toasts and drawer header keep firstName-first per UI-SPEC §Toast / §Drawer header.
  const displayNameMatrix = (u: MatrixRow['user']) =>
    `${u.lastName ?? ''} ${u.firstName ?? ''}`.trim() || u.email;

  const handleGrant = (tier?: AppAccessTier) => {
    if (!drawerState) return;
    const { row, system } = drawerState;
    grantMutation.mutate(
      { userId: row.user.id, system, tier },
      {
        onSuccess: () =>
          setToast({
            message: `Access grant dispatched for ${fullNameOf(row.user)}.`,
            variant: 'success',
          }),
        onError: (err: Error) => setToast({ message: err.message, variant: 'error' }),
      }
    );
  };

  const handleRetry = () => {
    if (!drawerState) return;
    const { row, system } = drawerState;
    retryMutation.mutate(
      { userId: row.user.id, system },
      {
        onSuccess: (resp) =>
          setToast({
            message:
              resp.mode === 'resume'
                ? 'Retry dispatched (resuming previous execution).'
                : 'Retry dispatched (fresh execution started).',
            variant: 'success',
          }),
        onError: (err: Error) => setToast({ message: err.message, variant: 'error' }),
      }
    );
  };

  const handleRevokeRequest = () => {
    setRevokeError(null);
    setRevokeMode('dispatch');
    setRevokeOpen(true);
  };

  const handleRevokeConfirm = (confirmation: string) => {
    if (!drawerState) return;
    const { row, system } = drawerState;
    revokeMutation.mutate(
      { userId: row.user.id, system, confirmation },
      {
        onSuccess: () => {
          setRevokeOpen(false);
          setRevokeError(null);
          setToast({
            message: `Access revoke dispatched for ${fullNameOf(row.user)}.`,
            variant: 'success',
          });
        },
        onError: (err: Error) => {
          // D-14: defensive CONFIRMATION_MISMATCH path
          if (
            err instanceof ApiClientError &&
            err.statusCode === 400 &&
            (err.details as { code?: string } | null | undefined)?.code === 'CONFIRMATION_MISMATCH'
          ) {
            setRevokeError('Confirmation failed — please retype the email and try again.');
          } else {
            setRevokeOpen(false);
            setToast({ message: err.message, variant: 'error' });
          }
        },
      }
    );
  };

  const clearFilters = () => {
    setSearchQuery('');
    setStatusFilter('all');
    setPage(1);
  };

  // Loading / empty / error gating
  if (isLoading && !data) return <LoadingPage />;

  const rows = data?.data ?? [];
  const pagination = data?.pagination;
  const hasActiveFilter = debouncedSearch.length > 0 || statusFilter !== 'all';

  return (
    <div className="space-y-6">
      <div>
        <button
          onClick={() => navigate('/admin')}
          className="text-sm text-gray-500 hover:text-gray-700 mb-1"
        >
          &larr; Back to Admin
        </button>
        <h1 className="text-2xl font-semibold text-gray-900">App Accesses</h1>
        <p className="text-sm text-gray-500 mt-1">Manage system access for all active users.</p>
      </div>

      {/* Search + Filter row */}
      <div className="flex flex-col sm:flex-row gap-3 mb-4">
        <input
          type="search"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="Search by name or email..."
          aria-label="Search users by name or email"
          className="w-full sm:max-w-xs rounded-lg border border-gray-300 px-4 py-2 text-sm text-gray-900 placeholder-gray-400 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
        />
        <div role="group" aria-label="Filter by access status" className="flex flex-wrap gap-2">
          {FILTER_OPTIONS.map((opt) => {
            const active = statusFilter === opt.value;
            return (
              <button
                key={opt.value}
                type="button"
                aria-pressed={active}
                onClick={() => setStatusFilter(opt.value)}
                className={
                  active
                    ? 'px-3 py-2 rounded-full border-2 border-primary-600 bg-white text-xs font-semibold text-primary-700 min-h-[44px] focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-1'
                    : 'px-3 py-2 rounded-full border border-gray-300 bg-white text-xs font-normal text-gray-600 hover:border-gray-400 hover:text-gray-700 min-h-[44px] focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-1'
                }
              >
                {opt.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Empty / Error / Matrix */}
      {error ? (
        <div className="py-12 text-center">
          <p className="text-sm font-semibold text-red-600">Failed to load the access matrix.</p>
          <p className="text-sm text-gray-500 mt-1">{(error as Error).message}</p>
          <Button variant="secondary" size="sm" onClick={() => refetch()} className="mt-4">
            Try again
          </Button>
        </div>
      ) : rows.length === 0 ? (
        hasActiveFilter ? (
          <div className="py-12 text-center">
            <p className="text-sm font-semibold text-gray-700">No users match your filters.</p>
            <p className="text-sm text-gray-500 mt-1">
              <button
                type="button"
                onClick={clearFilters}
                className="text-primary-600 hover:text-primary-700 underline focus:outline-none focus:ring-2 focus:ring-primary-500"
              >
                Clear filters
              </button>{' '}
              to see all users.
            </p>
          </div>
        ) : (
          <div className="py-12 text-center">
            <p className="text-sm text-gray-500">No active users found.</p>
          </div>
        )
      ) : (
        <>
          <div
            className="overflow-auto rounded-lg border border-gray-200 bg-white"
            style={{ maxHeight: 'calc(100vh - 260px)' }}
          >
            <table className="divide-y divide-gray-200 w-full">
              <thead className="bg-gray-50 sticky top-0 z-20">
                <tr>
                  <th
                    scope="col"
                    className="sticky left-0 z-30 bg-gray-50 px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase w-[240px] min-w-[240px]"
                  >
                    User
                  </th>
                  {SYSTEMS.map((sys) => (
                    <th
                      key={sys}
                      scope="col"
                      className="px-4 py-3 text-center text-xs font-semibold text-gray-500 uppercase whitespace-nowrap"
                    >
                      {!isBulkExcludedSystem(sys) ? (
                        <div className="flex flex-col items-center gap-1">
                          <input
                            type="checkbox"
                            aria-label={`Select all ${SYSTEM_DISPLAY_NAMES[sys]} cells on this page`}
                            checked={
                              rows.length > 0 &&
                              rows.every((r) => selectedSet.has(cellKey(r.user.id, sys)))
                            }
                            ref={(el) => {
                              if (el) {
                                const allKeys = rows.map((r) => cellKey(r.user.id, sys));
                                const selectedCount = allKeys.filter((k) =>
                                  selectedSet.has(k)
                                ).length;
                                el.indeterminate =
                                  selectedCount > 0 && selectedCount < allKeys.length;
                              }
                            }}
                            onChange={() => toggleColumn(sys, rows)}
                            className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                          />
                          <span>{SYSTEM_DISPLAY_NAMES[sys]}</span>
                        </div>
                      ) : (
                        SYSTEM_DISPLAY_NAMES[sys]
                      )}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {rows.map((row, idx) => {
                  const stripeBg = idx % 2 === 1 ? 'bg-gray-50' : 'bg-white';
                  const userFullName = fullNameOf(row.user);
                  return (
                    <tr
                      key={row.user.id}
                      className={idx % 2 === 1 ? 'bg-gray-50 hover:bg-gray-100' : 'hover:bg-gray-50'}
                    >
                      <th
                        scope="row"
                        className={`sticky left-0 z-10 px-4 py-2 text-left font-normal whitespace-nowrap w-[240px] min-w-[240px] ${stripeBg}`}
                      >
                        <div className="flex flex-col">
                          <span className="text-sm font-semibold text-gray-900">
                            {displayNameMatrix(row.user)}
                          </span>
                          <span className="text-xs text-gray-400">{row.user.email}</span>
                        </div>
                      </th>
                      {SYSTEMS.map((sys) => {
                        const cell: MatrixCell = row.cells[sys];
                        const sysLabel = SYSTEM_DISPLAY_NAMES[sys];
                        const cellAriaLabel = `${
                          cell.status === 'assumed_granted'
                            ? 'Assumed'
                            : cell.status === 'not_granted'
                              ? 'Not granted'
                              : cell.status.charAt(0).toUpperCase() + cell.status.slice(1)
                        } — ${sysLabel} access for ${userFullName}. Click to open details.`;
                        const isSelected =
                          !isBulkExcludedSystem(sys) && selectedSet.has(cellKey(row.user.id, sys));
                        return (
                          <td
                            key={sys}
                            className={`px-4 py-2 text-center cursor-pointer hover:bg-blue-50 focus-within:bg-blue-50${isSelected ? ' bg-blue-50' : ''}`}
                          >
                            <div className="flex flex-col items-center gap-1">
                              {!isBulkExcludedSystem(sys) && (
                                <input
                                  type="checkbox"
                                  aria-label={`Select ${sysLabel} for ${userFullName}`}
                                  checked={isSelected}
                                  onChange={(e) => {
                                    e.stopPropagation();
                                    toggleCell(row.user.id, sys);
                                  }}
                                  onClick={(e) => e.stopPropagation()}
                                  className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                                />
                              )}
                              <button
                                type="button"
                                onClick={(e) => {
                                  originatingButtonRef.current = e.currentTarget;
                                  setDrawerState({ row, system: sys });
                                }}
                                aria-label={cellAriaLabel}
                                className="w-full h-full flex items-center justify-center min-h-[44px] focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-inset rounded"
                              >
                                <AppAccessStatusChip
                                  status={cell.status}
                                  source={cell.source}
                                  systemLabel={sysLabel}
                                  userLabel={userFullName}
                                />
                              </button>
                            </div>
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Pagination summary + controls */}
          {pagination && (
            <div className="mt-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <p className="text-sm text-gray-500">
                  Showing {(pagination.page - 1) * pagination.pageSize + 1}&ndash;
                  {Math.min(pagination.page * pagination.pageSize, pagination.total)} of{' '}
                  {pagination.total} users
                </p>
                {isFetching && !isLoading && (
                  <div className="flex items-center gap-2 text-xs text-gray-400">
                    <Spinner size="sm" />
                    <span>Updating...</span>
                  </div>
                )}
              </div>
              <Pagination
                page={pagination.page}
                totalPages={pagination.totalPages}
                onPageChange={setPage}
              />
            </div>
          )}
        </>
      )}

      {/* Bulk action bar — floats at bottom when cells are selected (D-08) */}
      <BulkActionBar
        selectedCount={selectedSet.size}
        hasGrantEligible={eligibility.hasGrantEligible}
        hasRevokeEligible={eligibility.hasRevokeEligible}
        onGrant={() => setPendingDirection('grant')}
        onRevoke={() => setPendingDirection('revoke')}
        onClear={clearSelection}
      />

      {/* Bulk summary + confirmation modal */}
      {pendingDirection && (
        <BulkSummaryModal
          isOpen={true}
          onClose={() => setPendingDirection(null)}
          onConfirm={handleBulkConfirm}
          direction={pendingDirection}
          totalCount={bulkSummary.eligibleCount}
          perSystem={bulkSummary.perSystem}
          skipped={bulkSummary.skipped}
          isLoading={bulkGrantMutation.isPending || bulkRevokeMutation.isPending}
        />
      )}

      {/* Drawer — derive the row from the LIVE matrix query (not the click-time
          snapshot) so the cell status/button reflect dispatch + n8n-callback
          updates while the drawer stays open (polling/invalidation refetch the
          matrix). Falls back to the snapshot if the user paginated away. */}
      {drawerState &&
        (() => {
          const liveRow =
            data?.data.find((r) => r.user.id === drawerState.row.user.id) ?? drawerState.row;
          return (
            <AppAccessDrawer
              isOpen={true}
              onClose={closeDrawer}
              user={liveRow.user}
              system={drawerState.system}
              cell={liveRow.cells[drawerState.system]}
              originatingButtonRef={originatingButtonRef}
              onGrant={handleGrant}
              onRetry={handleRetry}
              onRevokeRequest={handleRevokeRequest}
            />
          );
        })()}

      {/* Revoke modal — layered on top of drawer when revokeOpen */}
      {drawerState && (
        <RevokeConfirmModal
          isOpen={revokeOpen}
          onClose={() => {
            setRevokeOpen(false);
            setRevokeError(null);
          }}
          onConfirm={handleRevokeConfirm}
          mode={revokeMode}
          user={drawerState.row.user}
          system={drawerState.system}
          systemDisplayName={SYSTEM_DISPLAY_NAMES[drawerState.system]}
          isLoading={revokeMutation.isPending}
          serverError={revokeError}
        />
      )}

      {/* Toast */}
      <AppToast toast={toast} onDismiss={() => setToast(null)} />
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Pagination — numbered buttons + Prev/Next + ellipsis (UI-SPEC §Pagination)
// Algorithm: show first, current ±2, last; ellipsis when gap > 1.
// ─────────────────────────────────────────────────────────────

function Pagination({
  page,
  totalPages,
  onPageChange,
}: {
  page: number;
  totalPages: number;
  onPageChange: (n: number) => void;
}) {
  if (totalPages <= 1) return null;

  const pages = computePageNumbers(page, totalPages);

  return (
    <nav aria-label="Pagination" className="flex items-center gap-1">
      <button
        type="button"
        onClick={() => onPageChange(Math.max(1, page - 1))}
        disabled={page <= 1}
        aria-disabled={page <= 1}
        className="px-3 py-2 text-sm rounded-md border border-gray-300 text-gray-700 hover:bg-gray-50 min-w-[36px] focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
      >
        &larr; Prev
      </button>
      {pages.map((p, i) =>
        p === 'ellipsis' ? (
          <span key={`ell-${i}`} className="px-2 py-2 text-sm text-gray-400" aria-hidden="true">
            &hellip;
          </span>
        ) : p === page ? (
          <button
            key={p}
            type="button"
            aria-current="page"
            className="px-3 py-2 text-sm rounded-md border-2 border-primary-600 bg-primary-50 text-primary-700 font-semibold min-w-[36px] focus:outline-none focus:ring-2 focus:ring-primary-500"
          >
            {p}
          </button>
        ) : (
          <button
            key={p}
            type="button"
            onClick={() => onPageChange(p)}
            className="px-3 py-2 text-sm rounded-md border border-gray-300 text-gray-700 hover:bg-gray-50 min-w-[36px] focus:outline-none focus:ring-2 focus:ring-primary-500"
          >
            {p}
          </button>
        )
      )}
      <button
        type="button"
        onClick={() => onPageChange(Math.min(totalPages, page + 1))}
        disabled={page >= totalPages}
        aria-disabled={page >= totalPages}
        className="px-3 py-2 text-sm rounded-md border border-gray-300 text-gray-700 hover:bg-gray-50 min-w-[36px] focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
      >
        Next &rarr;
      </button>
    </nav>
  );
}

function computePageNumbers(current: number, total: number): Array<number | 'ellipsis'> {
  if (total <= 7) {
    return Array.from({ length: total }, (_, i) => i + 1);
  }
  const pageSet = new Set<number>([
    1,
    total,
    current,
    current - 1,
    current + 1,
    current - 2,
    current + 2,
  ]);
  const sorted = Array.from(pageSet)
    .filter((p) => p >= 1 && p <= total)
    .sort((a, b) => a - b);
  return sorted.reduce<Array<number | 'ellipsis'>>((acc, n, i) => {
    if (i > 0 && n - (sorted[i - 1] as number) > 1) acc.push('ellipsis');
    acc.push(n);
    return acc;
  }, []);
}
